import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ArrowRightIcon, HelpCircle } from "lucide-react";
import { useState } from "react";

import { ColorContext } from "@/components/canvas-editor/components/Container";
import { useToast } from "@/components/hooks/use-toast";
import { MarketingFooterMinimal } from "@/components/MarketingFooterMinimal";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
// @ts-expect-error Dunno how to do types
import heroIllustration from "@/images/illustration.png?slice=1&x=300&y=0&height=1000&width=1000";
import {
  checkAuth,
  getCurrentUser,
  getLoginUrl,
  LoginSuccessType,
  setLoginSuccessType,
} from "@/lib/api";
import { removeInvisibleChars } from "@/lib/remove-invisible-chars";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    // validate and parse the search params into a typed state
    return {
      redirect: search?.redirect as string | undefined,
    };
  },
  beforeLoad: async () => {
    let isLoggedIn = false;

    try {
      await checkAuth();
      await getCurrentUser();
      isLoggedIn = true;
    } catch {
      // not logged in
    }

    if (isLoggedIn) {
      throw redirect({ to: "/app" });
    }
  },
  head: () => ({ meta: [{ title: "Login" }] }),
});

function LoginPage() {
  const search = Route.useSearch();
  const { toast } = useToast();
  const [username, setUsername] = useState("");
  const onLogin = useMutation({
    mutationFn: async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setLoginSuccessType(LoginSuccessType.DEFAULT);

      const loginUrl = await getLoginUrl();
      const formData = new FormData(e.target as HTMLFormElement);
      const username = removeInvisibleChars(formData.get("username") as string);

      window.location.href = `${loginUrl.replace(
        "http://",
        "https://",
      )}&redirect=${search.redirect}&login_hint=${username}`;
    },
    onError: (error) => {
      toast({
        title: "Error Logging In",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <>
      <main className="flex flex-col min-h-screen relative">
        <div className="flex-1 flex justify-center items-center h-full bg-teal-11 dark:bg-tealdark-10">
          <div className="px-4 flex flex-col items-center text-slate-normal">
            <div className="relative @container self-stretch flex flex-col gap-2">
              <div
                className="sharp-grotesk text-[90cqw] leading-[0.9] text-center"
                style={{
                  backgroundImage: `url(${heroIllustration})`,
                  backgroundSize: "100cqw",
                  backgroundClip: "text",
                  color: "transparent",
                  textShadow: "none",
                }}
              >
                G
              </div>
              <h1 className="text-slate-12 dark:text-slatedark-12 instrument-serif text-[30cqw] text-center mb-4 italic leading-none">
                Graze
              </h1>
            </div>

            <div className="flex-1 flex flex-col items-center pb-20">
              <form
                className="px-6 max-w-md w-full flex flex-col items-center gap-8"
                onSubmit={onLogin.mutate}
              >
                <ColorContext value="blue">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2 justify-center">
                      <label
                        htmlFor="username"
                        className="text-center text-sm font-semibold leading-none text-teal-contrast dark:text-tealdark-contrast"
                      >
                        Bluesky/Atproto Username
                      </label>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" className="p-0" size="smIcon">
                            <HelpCircle className="text-teal-contrast dark:text-tealdark-contrast" />
                            <span className="sr-only">Help</span>
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Help</DialogTitle>
                          </DialogHeader>
                          <div className="flex flex-col gap-2">
                            <p>
                              To login to graze you need a Bluesky account (or
                              any account on Atproto).
                            </p>
                            <p>
                              In the form on this page, enter your username you
                              use on Bluesky. You will then be redirected to
                              Bluesky where you can log in.
                            </p>
                            <p>
                              On that page you are <em>on Bluesky</em> and you
                              will log in with your Bluesky password.
                            </p>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="user.bsky.social"
                        name="username"
                        id="username"
                        autoComplete="username"
                        className="text-center max-w-xs lg:w-[50vw] text-lg! placeholder:text-lg! h-12!"
                        required
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-center gap-4 w-full">
                    <Button className="relative" type="submit" size="lg">
                      Login
                    </Button>
                    <Link
                      to="/login-old"
                      className="text-xs text-teal-contrast dark:text-tealdark-contrast flex items-center gap-1"
                    >
                      Log in with App Password Instead
                      <ArrowRightIcon />
                    </Link>
                  </div>
                </ColorContext>
              </form>
            </div>
          </div>
        </div>
      </main>
      <footer>
        <MarketingFooterMinimal />
      </footer>
    </>
  );
}
